import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ComoJoinLoyaltyBlockConfig } from '@front-platform/content-builder';
import { comoJoinLoyaltyBlockResolveConfig } from '@front-platform/content-builder';
import { some } from 'lodash';
import { Subject } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';
import { takeUntil, tap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import type { ComoJoinLoyaltyBlockScenarioContent } from '@front-platform/content-builder';
import { COMO_JOIN_LOYALTY_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

import { ComoUser } from '@core/models/como.model';

import { ComoService } from '@core/services/como/como.service';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
export interface ComoJoinLoyaltyBlockRuntimeConfig {}

export type ComoJoinLoyaltyBlockConfigInput = Partial<
  Omit<ComoJoinLoyaltyBlockConfig, keyof ComoJoinLoyaltyBlockRuntimeConfig> &
    ComoJoinLoyaltyBlockRuntimeConfig
>;
export type ComoJoinLoyaltyBlockResolvedConfig = Omit<
  ComoJoinLoyaltyBlockConfig,
  keyof ComoJoinLoyaltyBlockRuntimeConfig
> &
  Partial<ComoJoinLoyaltyBlockRuntimeConfig>;
export interface ComoJoinLoyaltyBlockContract extends ConfigComponentContract<
  ComoJoinLoyaltyBlockConfigInput,
  ComoJoinLoyaltyBlockResolvedConfig
> {}

@Component({
  selector: 'app-como-join-loyalty-block',
  templateUrl: './como-join-loyalty-block.component.html',
  standalone: false,
})
export class ComoJoinLoyaltyBlockComponent
  implements OnInit, OnDestroy, ComoJoinLoyaltyBlockContract
{
  readonly config = input<ComoJoinLoyaltyBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ComoJoinLoyaltyBlockConfig,
    ComoJoinLoyaltyBlockRuntimeConfig
  >(comoJoinLoyaltyBlockResolveConfig, () => ({ config: this.config() }));
  public registerUserToComo: UntypedFormControl;
  public comoUser?: ComoUser | null;
  public isLoading = true;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(COMO_JOIN_LOYALTY_BLOCK_COMPONENT);
  private readonly destroyed$ = new Subject<boolean>();

  constructor(
    private readonly comoService: ComoService,
    private cartDispatcher: CartStoreDispatcher,
    private marketplaceSelector: MarketplaceStoreSelector,
  ) {
    this.registerUserToComo = new UntypedFormControl();
    this.registerUserToComo.valueChanges.subscribe(val => {
      this.cartDispatcher.updateActive({
        register_user_to_como: val,
      });
    });
  }

  ngOnInit(): void {
    if (this.cb2PreviewState.isActive()) {
      this.previewScenario$
        .pipe(
          takeUntil(this.destroyed$),
          tap(scenario => {
            const content = previewScenarioData<ComoJoinLoyaltyBlockScenarioContent>(
              scenario,
              COMO_JOIN_LOYALTY_BLOCK_COMPONENT,
            );
            this.comoUser = (content?.comoUser ?? null) as ComoUser | null;
            this.isLoading = !content;
          }),
        )
        .subscribe();
      return;
    }

    const marketplace = this.marketplaceSelector.marketplace;
    const comoPluginEnabled = some(marketplace.plugins || [], plugin => {
      return plugin.type === 'como' && plugin.plugin_enabled;
    });

    if (comoPluginEnabled) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private load(): void {
    this.comoService.loadComoUser();
    this.comoService
      .getComoUser$()
      .pipe(
        takeUntil(this.destroyed$),
        tap(comoUser => {
          this.comoUser = comoUser;
          this.isLoading = false;
        }),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ComoJoinLoyaltyBlockComponent, { 'keyAliases': {} });
